import PropTypes from 'prop-types';
import { Heading, PageBlock, Text, textColors, textVariants, vars } from '@etg/wings';
import { Notification, variants } from '@eti/components';
import { css, cx } from '@eti/styles';
import { constants, phone } from '@eti/utils';
import { useProperty, useTranslation } from '@eti/providers';
import { AddOnOrder, PriceFormat } from '@eti/types';
import { bounds as boundsPropTypes } from '../../../widgets/trip-details/propTypes';
import { getMarketingCarrierNamesToDisplayInTaxBreakdown } from '../../../utils/carriers';
import ReceiptConfirmationActions from './ReceiptConfirmationActions';
import PriceSpecification from './ReceiptPriceSpecification';

const { unicodeControlCharacters, MOUSE_FLOW_CLASS_NAMES } = constants;
const { LRE, PDF } = unicodeControlCharacters;

const blockStyles = css`
  border-top: 1px solid ${vars.colors.divider};
`;

const headingClass = css`
  margin-bottom: 0.625rem;
`;

const contentClass = css`
  display: flex;
  flex-direction: column;

  @media (min-width: 48em) {
    margin: 0 auto;
    max-width: 65.3117%;
  }
`;

const listStyles = css`
  display: flex;
  flex-direction: column;
  font-size: 0.875rem;
  justify-content: space-between;

  @media (min-width: 48em) {
    flex-direction: row;
  }

  @media (min-width: 62em) {
    font-size: 1rem;
  }
`;

const columnStyles = css`
  @media (min-width: 48em) {
    flex: 0 0 48.6933%;
  }
`;

const descriptionStyles = css`
  margin-bottom: 1rem;
`;

const ReceiptDetails = ({
  addOnOrders,
  bounds,
  chargeCurrencyPriceFormat,
  customerDetails = {},
  cc2carrierBreakdown,
  discountPrice,
  hide,
  instalmentsTotalFee,
  isOtherChargeCurrency,
  isRyanairFlight,
  orderExtraProducts,
  bundles,
  orderedAt,
  orderReferenceSerialized,
  orderNumber,
  paymentTypes,
  priceInChargeCurrency,
  shouldShowAddress,
  totalPrice,
  totalPriceIncludingAddOnOrders,
  travelerPrices,
}) => {
  const { p } = useProperty();
  const { t } = useTranslation();

  const zeroTaxesCarriers = p('BookingFlow.HideTaxIfZero.Carriers');
  const carriersToDisplayInTaxBreakdown = p('BookingFlow.SupplierCostMarkupBreakdown.Carriers');
  const marketingCarrierNames = getMarketingCarrierNamesToDisplayInTaxBreakdown(
    bounds,
    carriersToDisplayInTaxBreakdown,
    zeroTaxesCarriers,
  );

  return (
    <PageBlock
      aria-labelledby="receipt-details-heading"
      as="section"
      className={blockStyles}
      withPaddingY
    >
      <div className={contentClass} data-testid="orderPage-ReceiptInformation-section">
        <Heading className={headingClass} id="receipt-details-heading" level={2}>
          {t('Receipt.ReceiptDetails.Heading')}
        </Heading>
        <dl className={listStyles}>
          <div className={columnStyles}>
            <Text as="dt" color={textColors.SECONDARY} variant={textVariants._12}>
              {t('Receipt.ReceiptDetails.Label.OrderNumber')}
            </Text>
            <dd
              className={descriptionStyles}
              data-testid="orderPage-ReceiptInformation-orderNumber"
            >
              {orderNumber}
            </dd>
            <Text
              as="dt"
              className={MOUSE_FLOW_CLASS_NAMES}
              color={textColors.SECONDARY}
              variant={textVariants._12}
            >
              {t('Receipt.ReceiptDetails.Label.CustomerName')}
            </Text>
            <dd
              className={cx(descriptionStyles, MOUSE_FLOW_CLASS_NAMES)}
              data-testid="orderPage-ReceiptInformation-customerName"
            >
              {customerDetails.name}
            </dd>
            {shouldShowAddress && (
              <>
                <Text as="dt" color={textColors.SECONDARY} variant={textVariants._12}>
                  {t('Receipt.ReceiptDetails.Label.CustomerAddress')}
                </Text>
                <dd
                  className={cx(descriptionStyles, MOUSE_FLOW_CLASS_NAMES)}
                  data-testid="orderPage-ReceiptInformation-address-wrapper"
                >
                  {customerDetails.street}
                  <br />
                  {customerDetails.zipCode} {customerDetails.city}
                  <br />
                  {customerDetails.nationality.name}
                </dd>
              </>
            )}
          </div>
          <div className={cx(columnStyles, MOUSE_FLOW_CLASS_NAMES)}>
            <Text as="dt" color={textColors.SECONDARY} variant={textVariants._12}>
              {t('Receipt.ReceiptDetails.Label.OrderDate')}
            </Text>
            <dd className={descriptionStyles} data-testid="orderPage-ReceiptInformation-orderDate">
              {orderedAt}
            </dd>
            <Text as="dt" color={textColors.SECONDARY} variant={textVariants._12}>
              {t('Receipt.ReceiptDetails.Label.CustomerEmail')}
            </Text>
            <dd
              className={descriptionStyles}
              data-testid="orderPage-ReceiptInformation-customerEmail"
            >
              {customerDetails.email}
            </dd>
            <Text as="dt" color={textColors.SECONDARY} variant={textVariants._12}>
              {t('Receipt.ReceiptDetails.Label.CustomerPhone')}
            </Text>
            <dd
              className={descriptionStyles}
              data-testid="orderPage-ReceiptInformation-customerPhone"
            >{`${LRE}${phone.addPrefix(customerDetails.phone)}${PDF}`}</dd>
          </div>
        </dl>
        {!isRyanairFlight && (
          <>
            <Notification onClose={hide} variant={variants.INFORMATION}>
              <p>{t('Receipt.CompanyDetails.ReceiptNotice')}</p>
            </Notification>
            <PriceSpecification
              addOnOrders={addOnOrders}
              bounds={bounds}
              bundles={bundles}
              cc2carrierBreakdown={cc2carrierBreakdown}
              chargeCurrencyPriceFormat={chargeCurrencyPriceFormat}
              discountPrice={discountPrice}
              instalmentsTotalFee={instalmentsTotalFee}
              isOtherChargeCurrency={isOtherChargeCurrency}
              marketingCarrierNames={marketingCarrierNames}
              orderExtraProducts={orderExtraProducts}
              paymentTypes={paymentTypes}
              priceInChargeCurrency={priceInChargeCurrency}
              totalPrice={totalPrice}
              totalPriceIncludingAddOnOrders={totalPriceIncludingAddOnOrders}
              travelerPrices={travelerPrices}
            />
            <ReceiptConfirmationActions
              addOnOrders={addOnOrders}
              customerEmail={customerDetails.email}
              orderNumber={orderNumber}
              orderReferenceSerialized={orderReferenceSerialized}
              purchaseDate={orderedAt}
            />
          </>
        )}
      </div>
    </PageBlock>
  );
};

ReceiptDetails.propTypes = {
  addOnOrders: PropTypes.arrayOf(AddOnOrder),
  bounds: boundsPropTypes,
  bundles: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      optionId: PropTypes.string,
      price: PropTypes.number,
      products: PropTypes.arrayOf(PropTypes.object),
      title: PropTypes.string,
    }),
  ),
  cc2carrierBreakdown: PropTypes.arrayOf(
    PropTypes.shape({
      carrierName: PropTypes.string,
      transactionAmount: PropTypes.shape({
        price: PropTypes.shape({
          currency: PropTypes.shape({
            exponent: PropTypes.number,
            name: PropTypes.string,
          }),
          value: PropTypes.number,
        }),
      }),
    }),
  ),
  chargeCurrencyPriceFormat: PriceFormat,
  customerDetails: PropTypes.shape({
    city: PropTypes.string,
    email: PropTypes.string,
    name: PropTypes.string,
    nationality: PropTypes.shape({
      code: PropTypes.string,
      name: PropTypes.string,
    }),
    phone: PropTypes.string,
    street: PropTypes.string,
    zipCode: PropTypes.string,
  }),
  discountPrice: PropTypes.number,
  hide: PropTypes.func,
  instalmentsTotalFee: PropTypes.number,
  isOtherChargeCurrency: PropTypes.bool,
  isRyanairFlight: PropTypes.bool,
  language: PropTypes.shape({
    code: PropTypes.string,
    id: PropTypes.number,
    name: PropTypes.string,
  }),
  orderedAt: PropTypes.string,
  orderExtraProducts: PropTypes.arrayOf(PropTypes.object),
  orderNumber: PropTypes.string,
  orderReferenceSerialized: PropTypes.string,
  paymentTypes: PropTypes.arrayOf(PropTypes.object),
  priceInChargeCurrency: PropTypes.number,
  shouldShowAddress: PropTypes.bool,
  totalPrice: PropTypes.number,
  totalPriceIncludingAddOnOrders: PropTypes.number,
  travelerPrices: PropTypes.arrayOf(
    PropTypes.shape({
      ageType: PropTypes.string,
      markup: PropTypes.number,
      price: PropTypes.number,
      quantity: PropTypes.number,
      totalPrice: PropTypes.number,
      totalPriceSum: PropTypes.number,
      vat: PropTypes.number,
    }),
  ),
};

export default ReceiptDetails;
